import React, { useState, useEffect } from 'react';
import { GlobeAltIcon, PencilSquareIcon, EyeIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import api from '../../utils/api';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA', warning:'#F59E0B' };

export default function SuperAdminWebsites() {
  const [websites, setWebsites] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('websites');

  useEffect(() => {
    Promise.all([
      api.get('/api/superadmin/websites').catch(() => ({ data: { websites: [] } })),
      api.get('/api/superadmin/enquiries').catch(() => ({ data: { enquiries: [] } })),
    ]).then(([w, e]) => {
      setWebsites(w.data.websites || w.data || []);
      setEnquiries(e.data.enquiries || e.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const STATUS_COLORS = { new:'#F59E0B', contacted:'#3B82F6', enrolled:'#10B981', lost:'#6B7280' };

  return (
    <Layout role="superadmin">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <h2 style={{ margin:0, fontSize:22, fontWeight:800, color:C.navy }}>Partner Websites</h2>
        <div style={{ display:'flex', gap:8 }}>
          {[['websites','🌐 Websites'],['enquiries','📬 Enquiries']].map(([val,label]) => (
            <button key={val} onClick={() => setTab(val)} style={{
              padding:'8px 18px', borderRadius:20, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
              background: tab===val ? C.navy : '#fff', color: tab===val ? '#fff' : C.grey,
              boxShadow: tab===val ? 'none' : '0 1px 4px rgba(0,0,0,0.08)'
            }}>{label}</button>
          ))}
        </div>
      </div>

      {tab === 'websites' && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#F9FAFB' }}>
                {['Partner','URL','Status','Enquiries','Completion','Last Updated','Actions'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:C.grey, borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding:40, textAlign:'center', color:C.grey }}>Loading...</td></tr>
              ) : websites.length === 0 ? (
                <tr><td colSpan={7} style={{ padding:40, textAlign:'center', color:C.grey }}>No partner websites yet</td></tr>
              ) : websites.map((w, i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:'14px 16px' }}>
                    <div style={{ fontWeight:600, fontSize:14 }}>{w.name}</div>
                  </td>
                  <td style={{ padding:'14px 16px', fontSize:13, color:C.teal }}>
                    {w.subdomain ? `${w.subdomain}.speakiq.uk` : w.custom_domain || '—'}
                  </td>
                  <td style={{ padding:'14px 16px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                      background: w.website_enabled ? '#ECFDF5' : '#F3F4F6',
                      color: w.website_enabled ? '#059669' : C.grey }}>
                      {w.website_enabled ? 'Live' : 'Disabled'}
                    </span>
                  </td>
                  <td style={{ padding:'14px 16px', fontSize:13 }}>{w.enquiry_count || 0}</td>
                  <td style={{ padding:'14px 16px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div style={{ height:6, width:80, background:'#E5E7EB', borderRadius:3 }}>
                        <div style={{ height:6, background:C.teal, borderRadius:3, width:`${w.completion_pct || 0}%` }} />
                      </div>
                      <span style={{ fontSize:12, color:C.grey }}>{w.completion_pct || 0}%</span>
                    </div>
                  </td>
                  <td style={{ padding:'14px 16px', fontSize:12, color:C.grey }}>
                    {w.updated_at ? new Date(w.updated_at).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td style={{ padding:'14px 16px' }}>
                    <div style={{ display:'flex', gap:8 }}>
                      {(w.subdomain || w.custom_domain) && (
                        <a href={`/school/${w.subdomain || w.custom_domain}`} target="_blank" rel="noopener noreferrer"
                           style={{ padding:'6px 10px', borderRadius:6, background:'#F0F7FA', border:`1px solid ${C.border}`, color:C.teal, fontSize:12, textDecoration:'none', display:'flex', alignItems:'center', gap:4 }}>
                          <EyeIcon style={{ width:14, height:14 }} /> Preview
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'enquiries' && (
        <div className="card" style={{ padding:0, overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#F9FAFB' }}>
                {['Name','Email','School','Programme Interest','Date','Status'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:700, color:C.grey, borderBottom:`1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:C.grey }}>Loading...</td></tr>
              ) : enquiries.length === 0 ? (
                <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:C.grey }}>No enquiries yet</td></tr>
              ) : enquiries.map((e, i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.border}` }}>
                  <td style={{ padding:'12px 16px', fontWeight:600, fontSize:14 }}>{e.name}</td>
                  <td style={{ padding:'12px 16px', fontSize:13, color:C.grey }}>{e.email}</td>
                  <td style={{ padding:'12px 16px', fontSize:13 }}>{e.school_name || e.tenant_name || '—'}</td>
                  <td style={{ padding:'12px 16px', fontSize:13 }}>{e.interested_package || '—'}</td>
                  <td style={{ padding:'12px 16px', fontSize:12, color:C.grey }}>
                    {e.created_at ? new Date(e.created_at).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td style={{ padding:'12px 16px' }}>
                    <span style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600,
                      background: STATUS_COLORS[e.status] + '20',
                      color: STATUS_COLORS[e.status] || C.grey }}>
                      {e.status || 'new'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
